import {
  CommonImage,
  TextSubtext,
} from '@porterin/feature/src/shared/types/CommonVM';

import {
  LeadFlowContentEntity,
  SpotFareEstimateContent,
} from '../../domain/entities/LeadFlowContentEntity';

export interface FieldLabels {
  pickup: string;
  drop: string;
  phone: string;
  name: string;
  requirement: string;
}

export interface ModalContent {
  title: string;
  icon: CommonImage;
}

export interface FieldIcons {
  pickup: CommonImage;
  drop: CommonImage;
  phone: CommonImage;
  name: CommonImage;
  requirement: CommonImage;
}

export interface IconLabel {
  icon: CommonImage;
  label: string;
}

export interface RequirementLabels {
  personal: string;
  business: string;
}

export interface FareEstimateContentVm {
  labels: FieldLabels;
  place_holders: FieldLabels;
  errors_msgs: FieldLabels;
  icons: FieldIcons;
  submit_icon_label: IconLabel;
  requirement_labels: RequirementLabels;
  consent_label: string;
  mobile_modal_content: ModalContent;
  vehcile_modal_content: ModalContent;
  city_title_desc: TextSubtext;
  location_icon: CommonImage;
  drop_down_icon: CommonImage;
}

interface FieldParam {
  label: string;
  place_holder: string;
  error_msg: string;
  icon: CommonImage;
}

interface FieldParams {
  pickup: FieldParam;
  drop: FieldParam;
  phone: FieldParam;
  name: FieldParam;
  requirement: FieldParam;
}

export interface FareEstimateContentEntity {
  field_params: FieldParams;
  get_estimate_icon: CommonImage;
  get_estimate_label: string;
  personal_use_label: string;
  business_use_label: string;
  consent_label: string;
  modal_title: string;
  vehcile_modal_title: string;
  modal_close_icon: CommonImage;
  city_title_desc: TextSubtext;
  location_icon: CommonImage;
  drop_down_icon: CommonImage;
}

const pickField = (
  params: FieldParams,
  field: 'label' | 'place_holder' | 'error_msg',
): FieldLabels => ({
  pickup: params.pickup[field],
  drop: params.drop[field],
  phone: params.phone[field],
  name: params.name[field],
  requirement: params.requirement[field],
});

export function getLeadFlowVm(
  data: FareEstimateContentEntity,
): FareEstimateContentVm {
  return {
    labels: pickField(data.field_params, 'label'),
    place_holders: pickField(data.field_params, 'place_holder'),
    errors_msgs: pickField(data.field_params, 'error_msg'),
    icons: {
      pickup: data.field_params.pickup.icon,
      drop: data.field_params.drop.icon,
      phone: data.field_params.phone.icon,
      name: data.field_params.name.icon,
      requirement: data.field_params.requirement.icon,
    },
    submit_icon_label: {
      icon: data.get_estimate_icon,
      label: data.get_estimate_label,
    },
    requirement_labels: {
      personal: data.personal_use_label,
      business: data.business_use_label,
    },
    consent_label: data.consent_label,
    mobile_modal_content: {
      title: data.modal_title,
      icon: data.modal_close_icon,
    },
    vehcile_modal_content: {
      title: data.vehcile_modal_title,
      icon: data.modal_close_icon,
    },
    city_title_desc: data.city_title_desc,
    location_icon: data.location_icon,
    drop_down_icon: data.drop_down_icon,
  };
}

export const REQUIREMENT = {
  PERSONAL: 0,
  BUSINESS: 1,
  HOUSE_SHIFT: 2,
} as const;

export interface RequirementOptionVm {
  title: string;
  value_label: string;
  desc: string;
  icon: CommonImage;
  requirement: number;
}

export interface RequirementOptionsVm {
  personal: RequirementOptionVm;
  business: RequirementOptionVm;
  requirement_title: string;
}

export interface SpotFareEstimateContentVm {
  labels: FieldLabels;
  place_holders: FieldLabels;
  errors_msgs: FieldLabels;
  icons: FieldIcons;
  submit_icon_label: IconLabel;
  requirement_options: RequirementOptionsVm;
  consent_label: string;
  city_title_desc: TextSubtext;
  mobile_modal_content: ModalContent;
}

export interface LeadFlowContentVm {
  fare_estimate_content: FareEstimateContentVm;
  spot_fare_estimate_content: SpotFareEstimateContentVm;
}

export function getSpotLeadFlowVm(
  data: SpotFareEstimateContent,
): SpotFareEstimateContentVm {
  return {
    labels: pickField(data.field_params, 'label'),
    place_holders: pickField(data.field_params, 'place_holder'),
    errors_msgs: pickField(data.field_params, 'error_msg'),
    icons: {
      pickup: data.field_params.pickup.icon,
      drop: data.field_params.drop.icon,
      phone: data.field_params.phone.icon,
      name: data.field_params.name.icon,
      requirement: data.field_params.requirement.icon,
    },
    submit_icon_label: {
      icon: data.get_estimate_icon,
      label: data.get_estimate_label,
    },
    requirement_options: {
      personal: {
        ...data.personal_option,
        requirement: REQUIREMENT.PERSONAL,
      },
      business: {
        ...data.business_option,
        requirement: REQUIREMENT.BUSINESS,
      },
      requirement_title: data.requirement_title,
    },
    consent_label: data.consent_label,
    city_title_desc: data.city_title_desc,
    mobile_modal_content: {
      title: data.modal_title,
      icon: data.modal_close_icon,
    },
  };
}

export function getVm(data: LeadFlowContentEntity): LeadFlowContentVm {
  return {
    fare_estimate_content: getLeadFlowVm(data.fare_estimate_content),
    spot_fare_estimate_content: getSpotLeadFlowVm(
      data.spot_fare_estimate_content,
    ),
  };
}
